import { gpCommon } from '../app';
import {
    createDictationSession,
    DictationCallbacks,
    DictationSession,
    isDictationSupported,
    mintDictationSession,
    MintResult,
    StartOptions,
} from '../dictation/dictation-session';
import { MintCache } from '../dictation/mint-cache';
import { isAiDictationEnabled } from '../util/feature-flags';
import {
    IAuthorisationCheckerService,
    IAuthorisationCheckerService_serviceName,
} from './authorisation-checker-with-caching-service';

/**
 * AngularJS wrapper around the framework-agnostic dictation session.
 *
 * Three jobs, and deliberately nothing else — the session module owns the
 * actual work:
 *
 *   1. AUTH. Supplies a fetch that carries a freshly-checked bearer token,
 *      mirroring what $httpAuth does per request. The cookie fallback on the
 *      server would cover us, but a header gets a token that was just checked
 *      rather than whatever the cookie last captured.
 *
 *   2. DIGEST. Session callbacks fire from WebRTC and fetch, outside Angular.
 *      $applyAsync both re-enters the digest and coalesces the delta storm —
 *      transcription deltas arrive several times a second and a $apply each
 *      would be wasteful.
 *
 *   3. EXCLUSIVITY. One session at a time across the whole app. Two mic
 *      buttons cannot both hold the microphone, so starting one stops the
 *      other rather than failing with a NotReadableError.
 */
export class DictationService {
    public static readonly serviceName = 'gpDictation';
    public static readonly $inject = ['$rootScope', IAuthorisationCheckerService_serviceName];

    /** The session that last claimed the microphone, if any. */
    private active: DictationSession | null = null;
    private activeOwner: object | null = null;

    /** One pre-minted session, shared app-wide (this service is a singleton, so
     *  every dictation surface reaches the same cache). */
    private mintCache = new MintCache();

    private static readonly SESSION_URL = '/api/ai-chat/dictation/session';

    constructor(
        private $rootScope: ng.IRootScopeService,
        private authChecker: IAuthorisationCheckerService,
    ) {}

    /**
     * Create a session owned by `owner` (the calling component instance).
     * Starting it stops whatever else was running.
     */
    public create(owner: object, callbacks: DictationCallbacks): DictationSession {
        const wrapped: DictationCallbacks = {
            onState: (state) => this.inDigest(() => callbacks.onState && callbacks.onState(state)),
            onInterim: (text) => this.inDigest(() => callbacks.onInterim && callbacks.onInterim(text)),
            onSegment: (id, text) => this.inDigest(() => callbacks.onSegment && callbacks.onSegment(id, text)),
            onSegmentRevised: (id, text) =>
                this.inDigest(() => callbacks.onSegmentRevised && callbacks.onSegmentRevised(id, text)),
            onError: (error) => this.inDigest(() => callbacks.onError && callbacks.onError(error)),
        };

        const session = createDictationSession(wrapped, {
            fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => this.authorizedFetch(input, init),
            getMint: async (): Promise<MintResult> => {
                const cached = this.mintCache.take();
                if (cached) return { ok: true, minted: { ...cached, prewarmed: true } };
                return mintDictationSession(
                    (input: RequestInfo | URL, init?: RequestInit) => this.authorizedFetch(input, init),
                    DictationService.SESSION_URL,
                );
            },
        });

        // Wrap start/stop so exclusivity is enforced however the caller drives it.
        const originalStart = session.start;
        const originalStop = session.stop;

        return {
            start: async (options?: StartOptions) => {
                if (this.active && this.activeOwner !== owner) {
                    await this.active.stop();
                }
                this.active = session;
                this.activeOwner = owner;
                await originalStart.call(session, options);
            },
            stop: async () => {
                await originalStop.call(session);
                if (this.activeOwner === owner) {
                    this.active = null;
                    this.activeOwner = null;
                }
            },
            finalize: async () => {
                await session.finalize();
                if (this.activeOwner === owner) {
                    this.active = null;
                    this.activeOwner = null;
                }
            },
            flushUsage: (useBeacon?: boolean) => session.flushUsage(useBeacon),
            get state() {
                return session.state;
            },
        };
    }

    /**
     * Mint-and-cache on a surface-open trigger, so the click-time
     * (docs/superpowers/specs/2026-08-06-dictation-ux-latency-design.md § A1)
     * mint round trip — and the Function cold start behind it — happen while
     * the user is still deciding what to say. Fire-and-forget; failures are
     * silent (the click falls back to a fresh mint).
     */
    public prewarmMint(): void {
        if (!isAiDictationEnabled() || !isDictationSupported()) return;
        this.mintCache.prewarm(() =>
            mintDictationSession(
                (input: RequestInfo | URL, init?: RequestInit) => this.authorizedFetch(input, init),
                DictationService.SESSION_URL,
            ),
        );
    }

    /** Whether any surface currently holds a live dictation session. */
    public isDictating(): boolean {
        return !!this.active && this.active.state !== 'idle' && this.active.state !== 'error';
    }

    /**
     * Finalize the active session, if any (spec § B2). The promise resolves
     * inside $applyAsync, AFTER the digest that copies the mic button's
     * isolate `text` through its `=` binding to the parent scope — so a
     * consumer reading its own model post-await always sees the tail.
     */
    public finalizeActive(): Promise<void> {
        const active = this.active;
        if (!active || !this.isDictating()) return Promise.resolve();
        return active
            .finalize()
            .then(
                () =>
                    new Promise<void>((resolve) => {
                        this.$rootScope.$applyAsync(() => resolve());
                    }),
            )
            .then(() => {
                if (this.active === active) {
                    this.active = null;
                    this.activeOwner = null;
                }
            });
    }

    /**
     * fetch() carrying the same Authorization header $httpAuth would send.
     * Calls to OpenAI carry their own ephemeral bearer already, so an existing
     * Authorization header is never overwritten.
     */
    private authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const headers = new Headers(init ? init.headers : undefined);
        if (headers.has('Authorization')) {
            return fetch(input, { ...init, headers });
        }

        return Promise.resolve(this.authChecker.checkAuthorisationIfNeeded())
            .then(
                (status) => (status && status.token ? status.token : ''),
                () => '',
            )
            .then((token) => {
                if (token) headers.set('Authorization', 'bearer ' + token);
                return fetch(input, { ...init, headers, credentials: 'same-origin' });
            });
    }

    /** Re-enter the digest without throwing if one is already in progress. */
    private inDigest(fn: () => void): void {
        fn();
        this.$rootScope.$applyAsync();
    }
}

gpCommon.service(DictationService.serviceName, DictationService);
